import { combineQueries, QueryEntity } from '@datorama/akita';
import { DateTimeService } from '@y42/date-time';
import { JobQuery } from '@y42/job';
import { OrgUserQuery } from '@y42/org-user/state';
import { isNotNullish } from '@y42/util/nullish';
import { map } from 'rxjs';
import { DatasourceTableState } from '../datasource-table/datasource-table.model';
import { DatasourceState } from '../datasource/datasource.model';
import { DatasourceJobSettingsValidationService } from './datasource-job-settings-validation/datasource-job-settings-validation.service';
import { DatasourceTableJobState, DatasourceTableJobUIModel } from './datasource-table-job.model';
import { DatasourceTableJobStore } from './datasource-table-job.store';

export abstract class DatasourceTableJobQuery extends JobQuery<DatasourceTableJobState, DatasourceTableJobUIModel> {
  constructor(
    store: DatasourceTableJobStore,
    dateTimeService: DateTimeService,
    private readonly grandParentQuery: QueryEntity<DatasourceState>,
    private readonly parentQuery: QueryEntity<DatasourceTableState>,
    orgUserQuery: OrgUserQuery,
    private readonly jobSettingsValidationService: DatasourceJobSettingsValidationService,
  ) {
    super(store, dateTimeService, orgUserQuery, (storeModel): DatasourceTableJobUIModel => {
      const datasourceTable = this.parentQuery.getEntity(storeModel.path);
      const datasource = datasourceTable ? this.grandParentQuery.getEntity(datasourceTable.$parent) : undefined;
      const validationResult =
        datasourceTable && datasource
          ? this.jobSettingsValidationService.validate(storeModel, datasourceTable, datasource)
          : undefined;
      return {
        ...storeModel,
        triggeredBy: this.getTriggeredBy(storeModel),
        columns:
          storeModel.details?.columns?.map((column) => ({
            ...column,
            // TODO: sanitize DatasourceColumnUIModel (the following props are from Visualizations)
            // For now we just apply default values for ui model required fields
            // The props should be hydrated on a separate query that injects this one
            isDeleted: false,
            hasDiffType: false,
            description: '',
            isCalculatedField: false,
          })) || [],
        datasourceTable: datasourceTable!,
        settingsValidationResult: validationResult,
        settingsValid: validationResult ? Object.values(validationResult).every((result) => result.isValid) : true,
      };
    });
  }

  protected readonly allJobs$ = combineQueries([
    this.selectAll(),
    this.parentQuery.selectActive(),
    this.orgUserQuery.selectAll(),
  ]).pipe(map(([items]) => items.map((item) => this.toUIModel(item)).filter(isNotNullish)));
}
